import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface SlackUser {
  id: string;
  name: string;
  real_name?: string;
  is_admin: boolean;
  is_bot: boolean;
  deleted: boolean;
  profile?: {
    display_name?: string;
    real_name?: string;
    image_48?: string;
  };
  updated?: number;
}

/**
 * Fetch Slack workspace members via slack-list-users action test endpoint.
 * Used as a fallback for slack-new-user-added trigger.
 *
 * Strategy:
 * - Fetch workspace members, filter out bots and deleted users
 * - Sort by most recently updated
 * - Transform each user into a trigger result matching the resultSchema
 *
 * Uses bot token (no user scope needed).
 */
export const slackUserAddedFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { connectorId } = ctx;

  if (!connectorId) return [];

  const response = await authenticatedFetch<{
    data: {
      result: {
        success: boolean;
        data: {
          ret: {
            ok: boolean;
            members: SlackUser[];
          };
        };
      };
    };
  }>(`${V2_ENDPOINT}/v1/actions/slack-list-users/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      inputs: { limit: 50 },
      connector: { id: connectorId, type: "slack" },
    }),
  });

  if (!response.ok) return [];
  const json = await response.json();

  const members = (json.data?.result?.data?.ret?.members ?? []) as SlackUser[];
  if (members.length === 0) return [];

  // Filter out bots and deleted users, sort by most recently updated.
  const humans = members
    .filter(u => !u.is_bot && !u.deleted && u.id !== "USLACKBOT")
    .sort((a, b) => (b.updated ?? 0) - (a.updated ?? 0))
    .slice(0, 10);

  return humans.map(u => ({
    triggeredAt: u.updated ? new Date(u.updated * 1000).toISOString() : new Date().toISOString(),
    eventType: "team_join",
    user: {
      id: u.id,
      name: u.name,
      real_name: u.real_name,
      is_admin: u.is_admin,
      profile: u.profile,
    },
    // Metadata for UI display
    _userName: u.name,
    _displayName: u.profile?.display_name || u.real_name || u.name,
    _isAdmin: u.is_admin,
    _avatarUrl: u.profile?.image_48 ?? "",
    _updatedAt: u.updated ? new Date(u.updated * 1000).toLocaleDateString() : "",
  }));
};
